// src/components/charts/assistant/ScatterChart.tsx
/**
 * Assistant Scatter Chart
 * 
 * A scatter chart optimized for the assistant's dark card UI with:
 * - Custom tooltip styling
 * - Point labels
 * - Hover emphasis effects
 */

import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { 
  ASSISTANT_CHART_THEME, 
  ASSISTANT_CHART_HEIGHT,
  getFormatter,
  type FormatterOptions,
} from "./theme";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface ScatterPoint {
  x: number;
  y: number;
  label: string;
}

export interface AssistantScatterChartProps {
  /** Data points */
  points: ScatterPoint[];
  /** X-axis formatting options */
  xFormatOptions?: FormatterOptions;
  /** Y-axis formatting options */
  yFormatOptions?: FormatterOptions;
  /** X-axis label */
  xAxisName?: string;
  /** Y-axis label */
  yAxisName?: string;
  /** Dot color */
  dotColor: string;
  /** Chart height (defaults to ASSISTANT_CHART_HEIGHT) */
  height?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const AssistantScatterChartInner: React.FC<AssistantScatterChartProps> = ({ 
  points, 
  xFormatOptions, 
  yFormatOptions, 
  xAxisName, 
  yAxisName, 
  dotColor,
  height = ASSISTANT_CHART_HEIGHT,
}) => {
  const xFormatter = getFormatter(xFormatOptions);
  const yFormatter = getFormatter(yFormatOptions);
  
  const option = useMemo(() => {
    return {
      backgroundColor: ASSISTANT_CHART_THEME.background,
      tooltip: {
        trigger: "item" as const,
        backgroundColor: ASSISTANT_CHART_THEME.tooltip.background,
        borderColor: ASSISTANT_CHART_THEME.tooltip.border,
        borderWidth: 1,
        padding: [10, 14],
        textStyle: {
          color: ASSISTANT_CHART_THEME.text.primary,
          fontSize: 12,
        },
        formatter: (params: unknown) => {
          const p = params as { data: [number, number, string] };
          const [x, y, label] = p.data;
          return `
            <div style="font-weight:500;margin-bottom:4px">${label}</div>
            <div style="color:rgba(255,255,255,0.6);font-size:11px">
              ${xAxisName || "X"}: ${xFormatter(x)}<br/>
              ${yAxisName || "Y"}: ${yFormatter(y)}
            </div>
          `;
        },
      },
      grid: {
        left: 8,
        right: 16,
        top: 20,
        bottom: 28,
        containLabel: true,
      },
      xAxis: {
        type: "value" as const,
        ...(xAxisName ? { name: xAxisName } : {}),
        nameTextStyle: {
          color: ASSISTANT_CHART_THEME.text.muted,
          fontSize: 10,
        },
        axisLabel: {
          color: ASSISTANT_CHART_THEME.text.muted,
          fontSize: 10,
          formatter: xFormatter,
        },
        splitLine: {
          lineStyle: {
            color: ASSISTANT_CHART_THEME.grid.line,
            type: "dashed" as const,
          },
        },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      yAxis: {
        type: "value" as const,
        ...(yAxisName ? { name: yAxisName } : {}),
        nameTextStyle: {
          color: ASSISTANT_CHART_THEME.text.muted,
          fontSize: 10,
        },
        axisLabel: {
          color: ASSISTANT_CHART_THEME.text.muted,
          fontSize: 10,
          formatter: yFormatter,
        },
        splitLine: {
          lineStyle: {
            color: ASSISTANT_CHART_THEME.grid.line,
            type: "dashed" as const,
          },
        },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: [{
        type: "scatter" as const,
        data: points.map(p => [p.x, p.y, p.label]),
        symbolSize: 12,
        itemStyle: {
          color: dotColor,
        },
        emphasis: {
          itemStyle: {
            borderColor: "#fff",
            borderWidth: 2,
          },
          scale: 1.4,
        },
        label: {
          show: true,
          position: "top" as const,
          formatter: (params: unknown) => {
            const p = params as { data: [number, number, string] };
            return p.data[2];
          },
          color: ASSISTANT_CHART_THEME.text.secondary,
          fontSize: 10,
          fontWeight: 500,
        },
      }],
      animationDuration: 500,
      animationEasing: "cubicOut" as const,
    };
  }, [points, xAxisName, yAxisName, xFormatter, yFormatter, dotColor]);

  return (
    <ReactECharts
      option={option}
      style={{ height, width: "100%" }}
      opts={{ renderer: "canvas" }}
      notMerge
    />
  );
};

// Custom comparison for memo
function arePropsEqual(
  prev: AssistantScatterChartProps,
  next: AssistantScatterChartProps
): boolean {
  // Quick checks for primitives
  if (
    prev.xAxisName !== next.xAxisName ||
    prev.yAxisName !== next.yAxisName ||
    prev.dotColor !== next.dotColor ||
    prev.height !== next.height
  ) {
    return false;
  }

  // Check points array
  if (prev.points.length !== next.points.length) return false;
  if (prev.points !== next.points) {
    for (let i = 0; i < prev.points.length; i++) {
      const p = prev.points[i]!;
      const n = next.points[i]!;
      if (p.x !== n.x || p.y !== n.y || p.label !== n.label) return false;
    }
  }

  // Check formatOptions
  const checkFormat = (
    p: AssistantScatterChartProps["xFormatOptions"],
    n: AssistantScatterChartProps["xFormatOptions"]
  ) => {
    if (p === n) return true;
    if (!p || !n) return false;
    return p.unit === n.unit && p.isChangeMetric === n.isChangeMetric;
  };

  if (!checkFormat(prev.xFormatOptions, next.xFormatOptions)) return false;
  if (!checkFormat(prev.yFormatOptions, next.yFormatOptions)) return false;

  return true;
}

const AssistantScatterChart = React.memo(AssistantScatterChartInner, arePropsEqual);

export default AssistantScatterChart;

